import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  CheckCircle,
  ExpandMore,
  Security,
  Speed,
  Storage,
  Api,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  marketingBandSx,
  marketingColor,
  marketingFont,
  marketingLine,
} from '../theme/marketingTokens';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ApiParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
}

interface ApiEndpoint {
  method: string;
  path: string;
  description: string;
  parameters: ApiParameter[];
  example: string;
  /** Plans whose API keys may call this endpoint */
  plans?: string;
}

interface CodeExample {
  language: string;
  title: string;
  code: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-tabpanel-${index}`}
      aria-labelledby={`api-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const ApiDocsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/app/dashboard');
    } else {
      navigate('/signup');
    }
  };

  const sidebarItems = t('public.docs.sidebarItems', { returnObjects: true }) as string[];
  const tabLabels = t('public.docs.tabs', { returnObjects: true }) as string[];
  const gettingStartedCards = t('public.docs.gettingStarted.cards', { returnObjects: true }) as { title: string; description: string }[];
  const authSteps = t('public.docs.authentication.steps', { returnObjects: true }) as { title: string; description: string }[];
  const apiEndpoints = t('public.docs.apiEndpoints', { returnObjects: true }) as ApiEndpoint[];
  const codeExamples = t('public.docs.codeExamples.examples', { returnObjects: true }) as CodeExample[];
  const rateLimitCards = t('public.docs.rateLimits.cards', { returnObjects: true }) as { value: string; title: string; description: string }[];
  const rateLimitHeaders = t('public.docs.rateLimits.headers', { returnObjects: true }) as { key: string; description: string }[];
  const errorStatusCodes = t('public.docs.errorHandling.statusCodes', { returnObjects: true }) as { code: string; description: string }[];
  const bestPractices = t('public.docs.errorHandling.bestPractices', { returnObjects: true }) as string[];
  const gettingStartedIcons = [Security, Speed, Storage, Api];
  const getStatusIcon = (code: string) => {
    if (code.startsWith('200')) {
      return <CheckCircle color="success" />;
    }

    const numericCode = Number.parseInt(code, 10);
    if (numericCode === 400) {
      return <Warning color="warning" />;
    }

    return <Warning color="error" />;
  };

  const responseExamples = {
    products: {
      success: true,
      products: [
        {
          id: "prod_123",
          model: "Sony Alpha 7 IV",
          brand: "Sony",
          price: 2499.99,
          priceFormatted: "$2,499.99",
          description: "Full-frame mirrorless camera with 33MP sensor",
          images: [
            {
              url: "https://example.com/image1.jpg",
              alt: "Sony Alpha 7 IV front view",
              displayOrder: 1
            }
          ],
          specifications: {
            sensor: "33MP Full-frame",
            video: "4K/60p",
            autofocus: "759 phase-detect points"
          },
          category: "Mirrorless Camera",
          productType: "camera",
          source: "camera_products_collection",
          createdAt: "2024-01-15T10:30:00Z",
          updatedAt: "2024-01-15T10:30:00Z"
        },
        {
          id: "prod_456",
          model: "Canon RF 24-70mm f/2.8L IS USM",
          brand: "Canon",
          price: 2299.99,
          priceFormatted: "$2,299.99",
          description: "Professional zoom lens for Canon RF mount",
          images: [
            {
              url: "https://example.com/lens1.jpg",
              alt: "Canon RF 24-70mm lens",
              displayOrder: 1
            }
          ],
          specifications: {
            focalLength: "24-70mm",
            aperture: "f/2.8",
            imageStabilization: "Yes"
          },
          category: "Lens",
          productType: "accessory",
          accessoryType: "lens",
          source: "products_collection",
          createdAt: "2024-01-15T10:30:00Z",
          updatedAt: "2024-01-15T10:30:00Z"
        }
      ],
      total: 1250,
      hasMore: true,
      pagination: {
        limit: 50,
        offset: 0,
        nextOffset: 50
      }
    },
    vendorIntegrations: {
      success: true,
      integrations: [
        {
          id: "int_123",
          vendorId: "sony",
          vendorName: "Sony",
          integrationType: "scraper",
          status: "active",
          isActive: true,
          syncFrequency: "daily",
          lastSyncAt: "2024-01-15T10:30:00Z",
          nextSyncAt: "2024-01-16T10:30:00Z",
          errorCount: 0,
          createdAt: "2024-01-01T00:00:00Z",
          updatedAt: "2024-01-15T10:30:00Z"
        }
      ]
    },
    mapPricing: {
      success: true,
      data: [
        {
          id: "map_123",
          brand: "Sony",
          model: "Alpha 7 IV",
          mapPrice: 2499.99,
          currency: "USD",
          effectiveDate: "2024-01-01",
          expirationDate: "2024-12-31",
          status: "active",
          createdAt: "2024-01-01T00:00:00Z"
        }
      ]
    }
  };

  return (
    <PublicLayout>
      <Box>
      {/* Header */}
      <Box
        sx={{
          ...marketingBandSx,
          borderTop: 'none',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
            {t('public.docs.header.title')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
            {t('public.docs.header.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Sidebar Navigation */}
          <Box sx={{ width: { xs: '100%', md: '300px' }, flexShrink: 0 }}>
            <Paper sx={{ p: 3, position: 'sticky', top: 100 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                {t('public.docs.sidebarTitle')}
              </Typography>
              <List dense>
                {sidebarItems.map((item, index) => (
                  <ListItem key={item} onClick={() => setTabValue(index)} sx={{ cursor: 'pointer' }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>

          {/* Main Content */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                {tabLabels.map((label) => (
                  <Tab key={label} label={label} />
                ))}
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  {t('public.docs.gettingStarted.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('public.docs.gettingStarted.intro')}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 4 }}>
                <AlertTitle>{t('public.docs.gettingStarted.baseUrlTitle')}</AlertTitle>
                {t('public.docs.gettingStarted.baseUrlNote')}{' '}
                <Typography component="span" sx={{ fontFamily: marketingFont.mono, bgcolor: marketingColor.surfaceSelected, px: 1, borderRadius: 0.5 }}>https://api.motzulabs.com/api</Typography>
              </Alert>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(250px, 1fr))' }, gap: 3 }}>
                {gettingStartedCards.map((card, index) => {
                  const Icon = gettingStartedIcons[index] ?? Security;
                  return (
                    <Card key={card.title}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Icon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6">{card.title}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {card.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{ py: 2, px: 4 }}
                >
                  {t('public.docs.gettingStarted.cta')}
                </Button>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {t('public.docs.authentication.title')}
              </Typography>
              
              <Typography variant="body1" paragraph>
                {t('public.docs.authentication.intro')}
              </Typography>

              <Paper sx={{ p: 3, mb: 4, bgcolor: marketingColor.surface, border: `1px solid ${marketingLine.hairline}` }}>
                <Typography variant="h6" gutterBottom>
                  {t('public.docs.authentication.authHeaderTitle')}
                </Typography>
                <Typography sx={{ display: 'block', p: 2, bgcolor: marketingColor.ink, border: `1px solid ${marketingLine.hairline}`, borderRadius: 1, fontFamily: marketingFont.mono }}>
                  {t('public.docs.authentication.authHeaderValue')}
                </Typography>
              </Paper>

              <Alert severity="warning" sx={{ mb: 4 }}>
                <AlertTitle>{t('public.docs.authentication.warningTitle')}</AlertTitle>
                {t('public.docs.authentication.warningText')}
              </Alert>

              <Typography variant="h6" gutterBottom>
                {t('public.docs.authentication.stepsTitle')}
              </Typography>
              <List>
                {authSteps.map((step) => (
                  <ListItem key={step.title}>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={step.title}
                      secondary={step.description}
                    />
                  </ListItem>
                ))}
              </List>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {t('public.docs.endpoints.title')}
              </Typography>
              
              {apiEndpoints.map((endpoint, index) => (
                <Accordion key={index} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Chip
                        label={endpoint.method}
                        color={
                          endpoint.method === 'GET'
                            ? 'success'
                            : endpoint.method === 'DELETE'
                              ? 'error'
                              : endpoint.method === 'PUT'
                                ? 'warning'
                                : 'primary'
                        }
                        size="small"
                        sx={{ mr: 2 }}
                      />
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {endpoint.path}
                      </Typography>
                      {endpoint.plans && (
                        <Chip
                          label={`${t('public.docs.endpoints.plansLabel')}: ${endpoint.plans}`}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      {endpoint.description}
                    </Typography>
                    
                    {endpoint.parameters && endpoint.parameters.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          {t('public.docs.endpoints.parametersTitle')}
                        </Typography>
                        <Stack spacing={2}>
                          {endpoint.parameters.map((param, paramIndex) => (
                            <Box key={paramIndex}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Chip
                                  label={param.name}
                                  size="small"
                                  color={param.required ? 'error' : 'default'}
                                  sx={{ mr: 2 }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                                  {param.type}
                                </Typography>
                                {param.required && (
                                  <Chip label={t('public.docs.endpoints.requiredLabel')} size="small" color="error" />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                {param.description}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Typography variant="h6" gutterBottom>
                      {t('public.docs.endpoints.exampleTitle')}
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: marketingColor.surface, border: `1px solid ${marketingLine.hairline}` }}>
                      <Typography sx={{ display: 'block', whiteSpace: 'pre-wrap', fontFamily: marketingFont.mono }}>
                        {endpoint.example}
                      </Typography>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              ))}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {t('public.docs.codeExamples.title')}
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))' }, gap: 3 }}>
                {codeExamples.map((example, index) => (
                  <Card key={index}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {example.title}
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: marketingColor.surface, border: `1px solid ${marketingLine.hairline}`, mb: 2 }}>
                        <Typography sx={{ display: 'block', whiteSpace: 'pre-wrap', fontSize: '0.875rem', fontFamily: marketingFont.mono }}>
                          {example.code}
                        </Typography>
                      </Paper>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                {t('public.docs.codeExamples.responseFormatTitle')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('public.docs.codeExamples.responseFormatIntro')}
              </Typography>
              
              <Paper sx={{ p: 2, bgcolor: marketingColor.surface, border: `1px solid ${marketingLine.hairline}`, mb: 3 }}>
                <Typography sx={{ display: 'block', whiteSpace: 'pre-wrap', fontFamily: marketingFont.mono }}>
                  {JSON.stringify(responseExamples.products, null, 2)}
                </Typography>
              </Paper>

              <Typography variant="body2" color="text.secondary">
                {t('public.docs.codeExamples.responseFormatNote')}
              </Typography>
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {t('public.docs.rateLimits.title')}
              </Typography>
              
              <Typography variant="body1" paragraph>
                {t('public.docs.rateLimits.intro')}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(200px, 1fr))' }, gap: 3, mb: 4 }}>
                {rateLimitCards.map((card) => (
                  <Card key={card.title}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="primary" gutterBottom>
                        {card.value}
                      </Typography>
                      <Typography variant="h6" gutterBottom>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Alert severity="info">
                <AlertTitle>{t('public.docs.rateLimits.headersTitle')}</AlertTitle>
                {t('public.docs.rateLimits.headersIntro')}
                <ul>
                  {rateLimitHeaders.map((header) => (
                    <li key={header.key}>
                      <Typography component="span" sx={{ fontFamily: marketingFont.mono, bgcolor: marketingColor.surfaceSelected, px: 0.5, borderRadius: 0.25 }}>
                        {header.key}
                      </Typography>
                      {' '} - {header.description}
                    </li>
                  ))}
                </ul>
              </Alert>
            </TabPanel>

            <TabPanel value={tabValue} index={5}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {t('public.docs.errorHandling.title')}
              </Typography>
              
              <Typography variant="body1" paragraph>
                {t('public.docs.errorHandling.intro')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('public.docs.errorHandling.statusCodesTitle')}
                  </Typography>
                  <List dense>
                    {errorStatusCodes.map((status) => (
                      <ListItem key={status.code}>
                        <ListItemIcon>
                          {getStatusIcon(status.code)}
                        </ListItemIcon>
                        <ListItemText 
                          primary={status.code}
                          secondary={status.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('public.docs.errorHandling.errorFormatTitle')}
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: marketingColor.surface, border: `1px solid ${marketingLine.hairline}` }}>
                    <Typography sx={{ display: 'block', whiteSpace: 'pre-wrap', fontFamily: marketingFont.mono }}>
{`{
  "error": {
    "message": "Invalid API key",
    "code": "INVALID_API_KEY",
    "statusCode": 401,
    "timestamp": "2024-01-15T10:30:00Z"
  }
}`}
                    </Typography>
                  </Paper>
                </Box>
              </Box>

              <Alert severity="warning" sx={{ mt: 4 }}>
                <AlertTitle>{t('public.docs.errorHandling.bestPracticesTitle')}</AlertTitle>
                <ul>
                  {bestPractices.map((practice) => (
                    <li key={practice}>{practice}</li>
                  ))}
                </ul>
              </Alert>
            </TabPanel>
          </Box>
        </Box>
      </Container>
      </Box>
    </PublicLayout>
  );
};

export default ApiDocsPage;
